import React, { useState } from "react";
import SEO from "@/components/SEO";
import axios from "axios";
import { toast } from "sonner";
import { PageHero } from "@/components/sections/PageHero";
import { Reveal } from "@/components/effects/Reveal";
import { SERVICE_CATEGORIES_FORM } from "@/lib/data";
import { Camera, Video, Layers, Calendar, MapPin, Users, Mail, Phone, User, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "Photos", label: "Photos", icon: Camera },
  { value: "Video", label: "Video", icon: Video },
  { value: "Photos and Video", label: "Photos & Video", icon: Layers },
];

const Field = ({ label, icon: Icon, children, testId }) => (
  <label className="block" data-testid={`field-${testId}`}>
    <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
      {Icon && <Icon className="w-3 h-3 text-white/40" />}
      {label}
    </span>
    {children}
  </label>
);

const inputBase =
  "w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-all font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]";

export default function HireUs() {
  const [form, setForm] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    event_type: "",
    event_date: "",
    event_location: "",
    estimated_people: "",
    service_needed: "Photos and Video",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surname || !form.email || !form.phone || !form.event_type) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/hire`, form);
      setDone(true);
      toast.success("Inquiry sent — we’ll be in touch within 24 hours.");
      setForm({
        name: "", surname: "", email: "", phone: "", event_type: "", event_date: "",
        event_location: "", estimated_people: "", service_needed: "Photos and Video", message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-hire">
      <SEO title="Hire Us — WelkomBiz Photography" description="Hire WelkomBiz Photography for your sports event, wedding, matric farewell, baby shower, corporate shoot or promotional video in Welkom." canonicalPath="/hire" />
      <PageHero
        eyebrow="Hire WelkomBiz"
        title="Book the team that"
        accent="captures it all"
        suffix="."
        subtitle="Tell us about your event. Whether it’s a wedding, sports day, corporate launch or lifestyle shoot — we’ll respond within 24 hours with a tailored proposal."
      />

      {/* Service categories */}
      <section className="relative pb-12">
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10">
          <div className="flex flex-wrap gap-2 justify-center">
            {SERVICE_CATEGORIES_FORM.map((s, i) => (
              <Reveal key={s} delay={i * 30}>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, event_type: s }))}
                  data-testid={`service-cat-${i}`}
                  className={`text-[11px] tracking-luxe uppercase px-4 py-2 rounded-full border transition-all ${
                    form.event_type === s
                      ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white"
                      : "border-white/15 text-white/65 hover:text-white hover:border-white/30"
                  }`}
                >
                  {s}
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="relative pb-32">
        <div className="max-w-[920px] mx-auto px-6 sm:px-10">
          <Reveal>
            <form
              data-testid="hire-form"
              onSubmit={submit}
              className="glass-strong chrome-border rounded-md p-6 sm:p-10 relative overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,46,147,0.18), transparent 70%)" }} />
              <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(24,211,255,0.16), transparent 70%)" }} />

              {done && (
                <div className="relative mb-6 flex items-center gap-3 px-4 py-3 rounded-md border border-[#18d3ff]/30 bg-[#18d3ff]/5 text-[#cdf3ff]">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm font-body">Inquiry received. We’ll reach out shortly.</span>
                </div>
              )}

              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name" icon={User} testId="name">
                  <input data-testid="input-name" required value={form.name} onChange={update("name")} placeholder="Your first name" className={inputBase} />
                </Field>
                <Field label="Surname" icon={User} testId="surname">
                  <input data-testid="input-surname" required value={form.surname} onChange={update("surname")} placeholder="Your surname" className={inputBase} />
                </Field>
                <Field label="Email" icon={Mail} testId="email">
                  <input data-testid="input-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputBase} />
                </Field>
                <Field label="Phone Number" icon={Phone} testId="phone">
                  <input data-testid="input-phone" required value={form.phone} onChange={update("phone")} placeholder="+27 ..." className={inputBase} />
                </Field>
                <Field label="Type of Event" icon={Layers} testId="event-type">
                  <input data-testid="input-event-type" required value={form.event_type} onChange={update("event_type")} placeholder="Wedding, Sports day, Corporate launch…" className={inputBase} />
                </Field>
                <Field label="Event Date" icon={Calendar} testId="event-date">
                  <input data-testid="input-event-date" type="date" value={form.event_date} onChange={update("event_date")} className={inputBase} />
                </Field>
                <Field label="Event Location" icon={MapPin} testId="event-location">
                  <input data-testid="input-event-location" value={form.event_location} onChange={update("event_location")} placeholder="City / venue" className={inputBase} />
                </Field>
                <Field label="Estimated People" icon={Users} testId="estimated-people">
                  <input data-testid="input-estimated-people" value={form.estimated_people} onChange={update("estimated_people")} placeholder="50, 200, 1000+" className={inputBase} />
                </Field>
              </div>

              <div className="relative mt-8">
                <p className="text-[10px] tracking-luxe uppercase text-white/50 mb-3">Service Needed</p>
                <div className="grid grid-cols-3 gap-2">
                  {SERVICE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`service-needed-${opt.value.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setForm({ ...form, service_needed: opt.value })}
                      className={`group relative px-4 py-4 rounded-md border text-sm font-body transition-all flex flex-col items-center gap-2 ${
                        form.service_needed === opt.value
                          ? "border-transparent bg-gradient-to-br from-[#ff2e93]/15 to-[#18d3ff]/15 chrome-border text-white"
                          : "border-white/10 text-white/65 hover:text-white hover:border-white/25"
                      }`}
                    >
                      <opt.icon className="w-4 h-4" />
                      <span className="text-[11px] tracking-luxe uppercase">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative mt-8">
                <Field label="Message / Additional Information" testId="message">
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell us more about your event, your vision, references, or anything we should know."
                    className={`${inputBase} resize-none`}
                  />
                </Field>
              </div>

              <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-[11px] tracking-luxe uppercase text-white/40">
                  We respond within 24 hours.
                </p>
                <button data-testid="hire-submit" type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? "Sending…" : "Send Inquiry"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
